import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  computed,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { environment } from '@env/environment';
import { provideEffects } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { LogLevel, provideAuth } from 'angular-auth-oidc-client';

import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';

import { GfeErrorHandler } from '@core/error-handler/gfe-error-handler';
import { apiCaseRequestInterceptor } from '@core/interceptors/api-case-request.interceptor';
import { apiResponseCaseInterceptor } from '@core/interceptors/api-case-response.interceptor';
import { authInterceptor } from '@core/interceptors/gbe-auth.interceptor';
import { gbeErrorInterceptor } from '@core/interceptors/gbe-error.interceptor';
import { registerAllEntityExtensions } from '@core/models/entities/implementations/entity-extensions-registry';
import { LoginErrorService } from '@core/services/auth/login-error.service';
import { ErrorCatalogLoader } from '@core/services/error-catalog/error-catalog.loader';
import { ICONS } from '@core/services/icons/icons.service';
import { PageTitleService } from '@core/services/page-title/page-title.service';
import { RouteStateSyncService } from '@core/services/route-state-sync/route-state-sync.service';
import { ThemeService } from '@core/services/theme/theme.service';
import { GenericEntityRegistryService } from '@core/services/user-models-registry/generic-entity-registry.service';
import { UserPreferencesFlushService } from '@core/services/user-preferences/user-preferences-flush.service';
import { TagsLabelsEffects } from '@core/store/admin/tags-labels/tags-labels.effects';
import { rehydrateAuth } from '@core/store/auth/auth.actions';
import { AuthEffects } from '@core/store/auth/auth.effects';
import { CommentsConfigEffects } from '@core/store/comments-config/comments-config.effects';
import { PopupDataVisualizationReplicator } from '@core/store/data-visualization-popup/data-visualization-popup.replicator';
import { VisualizationEffects } from '@core/store/data-visualization/data-visualization.effects';
import { DataspaceEffects } from '@core/store/dataspace/dataspace.effects';
import { FeatureFlagsEffects } from '@core/store/feature-flags/feature-flags.effects';
import { OptimizationHistoryEffects } from '@core/store/optimization-history/optimization-history.effects';
import { PlanEditSessionEffects } from '@core/store/plan-edit/plan-edit-session.effects';
import { selectPlanChanges } from '@core/store/plan-edit/plan-edit-session.selectors';
import { PlanEditEffects } from '@core/store/plan-edit/plan-edit.effects';
import { PlanWatchEffects } from '@core/store/plan-edit/plan-watch.effects';
import { PlanFindingsContextEffects } from '@core/store/plan-findings/plan-findings-context.effects';
import { PlanFindingsEffects } from '@core/store/plan-findings/plan-findings.effects';
import { PlanEffects } from '@core/store/plan/plan.effects';
import { UserPreferencesEffects } from '@core/store/user-preferences/user-preferences.effects';
import { resolveLanguage } from '@core/utils/language.utils';

import { availableLanguages } from '@shared/types/translation.type';

import { providePlanPanelSection } from '@features/components/plan/plan-panel/plan-panel-section';
import { ChangesHeaderActionsComponent } from '@features/components/plan/plan-panel/sections/changes/changes-header-actions.component';
import {
  ChangesSectionComponent,
  changesSectionVisible
} from '@features/components/plan/plan-panel/sections/changes/changes-section.component';
import { FindingsSectionComponent } from '@features/components/plan/plan-panel/sections/findings/findings-section.component';
import { KpisHeaderActionsComponent } from '@features/components/plan/plan-panel/sections/kpis/kpis-header-actions.component';
import { KpisSectionComponent } from '@features/components/plan/plan-panel/sections/kpis/kpis-section.component';
import { PlanInfoOverviewLinkComponent } from '@features/components/plan/plan-panel/sections/plan-info/plan-info-overview-link.component';
import { PlanInfoSectionComponent } from '@features/components/plan/plan-panel/sections/plan-info/plan-info-section.component';

import { storeConfig } from './app-store.config';
import { OIDC_UNAUTHORIZED_ROUTE, routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      // Before rehydrateAuth: checkAuth() consumes the login callback URL.
      inject(LoginErrorService).captureFrom(window.location.href);
      const store = inject(Store);
      store.dispatch(rehydrateAuth());
      const translate = inject(TranslateService);
      translate.addLangs(Array.from(availableLanguages));
      inject(EntityLabelsTranslationService);
      registerAllEntityExtensions(inject(GenericEntityRegistryService));
      // Initialize route state synchronization
      const routeStateSync = inject(RouteStateSyncService);
      routeStateSync.initialize();
      // Flush pending preference writes when the tab is hidden/closed.
      inject(UserPreferencesFlushService).initialize();
      inject(ThemeService).initialize();
      // Not awaited: a finding renders without annotations, so nothing should wait on them.
      inject(ErrorCatalogLoader).load();
      const defaultLanguage = availableLanguages[0];
      const savedLang = localStorage.getItem('gfe-lang');
      const requested = savedLang ?? navigator.language;
      const languageCode = resolveLanguage(requested);
      if (!languageCode) {
        console.warn(
          `Language ${requested} is not supported, defaulting to ${defaultLanguage}.`
        );
        translate.setFallbackLang(defaultLanguage);
        return translate.use(defaultLanguage);
      }
      translate.setFallbackLang(languageCode);
      const useResult = translate.use(languageCode);
      inject(PageTitleService).initialize();
      return useResult;
    }),
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GfeErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        apiCaseRequestInterceptor,
        authInterceptor,
        gbeErrorInterceptor,
        apiResponseCaseInterceptor
      ])
    ),
    storeConfig,
    providePlanPanelSection({
      id: 'plan',
      titleKey: 'features.plan.panel.sections.plan',
      icon: ICONS.info,
      order: 10,
      expandedColumn: 'left',
      component: PlanInfoSectionComponent,
      headerActions: PlanInfoOverviewLinkComponent
    }),
    providePlanPanelSection({
      id: 'kpis',
      titleKey: 'features.plan.panel.sections.kpis',
      icon: ICONS.chartBar,
      order: 20,
      expandedColumn: 'left',
      component: KpisSectionComponent,
      headerActions: KpisHeaderActionsComponent
    }),
    providePlanPanelSection({
      id: 'changes',
      titleKey: 'features.plan.panel.sections.changes',
      icon: ICONS.clockCounterClockwise,
      order: 30,
      expandedColumn: 'left',
      component: ChangesSectionComponent,
      headerActions: ChangesHeaderActionsComponent,
      visible: (injector) => {
        const changes = injector.get(Store).selectSignal(selectPlanChanges);
        return computed(() => changesSectionVisible(changes()));
      }
    }),
    providePlanPanelSection({
      id: 'findings',
      titleKey: 'features.plan.panel.sections.findings',
      icon: ICONS.warning,
      order: 40,
      expandedColumn: 'right',
      component: FindingsSectionComponent
    }),
    provideEffects([
      AuthEffects,
      DataspaceEffects,
      FeatureFlagsEffects,
      PlanEffects,
      PlanFindingsEffects,
      PlanFindingsContextEffects,
      PlanEditEffects,
      PlanWatchEffects,
      PlanEditSessionEffects,
      PopupDataVisualizationReplicator,
      VisualizationEffects,
      TagsLabelsEffects,
      CommentsConfigEffects,
      UserPreferencesEffects,
      OptimizationHistoryEffects
    ]),
    provideTranslateService({
      fallbackLang: availableLanguages[0],
      loader: provideTranslateHttpLoader({
        prefix: './i18n/',
        suffix: '.json',
        useHttpBackend: true
      })
    }),
    provideAuth({
      config: {
        authority: environment.keycloak.authority,
        redirectUrl: environment.keycloak.redirectUrl,
        postLogoutRedirectUri: environment.keycloak.postLogoutRedirectUri,
        clientId: environment.keycloak.clientId,
        scope: environment.keycloak.scope,
        responseType: 'code',
        unauthorizedRoute: OIDC_UNAUTHORIZED_ROUTE,
        silentRenew: true,
        useRefreshToken: true,
        logLevel: environment.production ? LogLevel.Error : LogLevel.Debug
      }
    })
  ]
};
